import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import useStyles from 'isomorphic-style-loader/useStyles';
import { Wrench } from 'lucide-react';
import { useMemo, useRef } from 'react';
import { Popup } from 'semantic-ui-react';

import type { PaginationSort } from '@models';
import ContactCard from '@components/contact-card';
import ContextPanel, {
  RowDetailPanelTrigger,
  useRowDetailPanel,
} from '@components/context-panel';
import EmptyPlaceholder from '@components/empty-placeholder';
import Err from '@components/err';
import Link from '@components/link';
import { usePageContext } from '@components/page/page-context';
import RelativeTimestamp from '@components/relative-timestamp';
import type {
  ColumnDef,
  DataTableInstance,
  SortingState,
} from '@components/table';
import { ServerDataTable } from '@components/table';
import TruncateSegments from '@components/truncate-segments';
import TruncateText from '@components/truncate-text';
import type { InfiniteScrollOptions } from '@components/virtual-list';
import {
  AFFECTED_PACKAGES_COLUMN_DESCRIPTION,
  AffectedPackagesPanel,
  AffectedPackagesPanelHeader,
} from '@features/applications/common/affected-packages-panel';
import ApplicationVersionStatusDropdown, {
  ApplicationVersionStatusDescription,
} from '@features/applications/common/application-version-status';
import { convertToTableSort } from '@utils/common';

import { noImpactedVersionsMessage } from '../quick-search.utils';

import type { QuickSearchVulnerabilityListItem } from './vulnerability-search.models';
import s from './impacted-app-versions-table.scss';

const DETAIL_PANEL_OPTIONS = { padded: false, width: '32rem' };

const PANEL_ENTITY = 'application version';

const getRowKey = (row: QuickSearchVulnerabilityListItem) =>
  `${row.application.id}/${row.appVersion.id}`;

const getRowTitle = (row: QuickSearchVulnerabilityListItem) =>
  `${row.application.name} ${row.appVersion.name}`;

// The share of the line the version name claims, against the application name's
// 1. The column favors the version, which is what tells its rows apart while the
// application repeats down them; the panel header shows a single row, where
// losing either half is as bad, so it splits the line evenly.
const COLUMN_VERSION_NAME_WEIGHT = 3;
const HEADER_VERSION_NAME_WEIGHT = 1;

const getNameSegments = (
  row: QuickSearchVulnerabilityListItem,
  versionNameWeight: number,
) => [
  { content: row.application.name },
  { content: row.appVersion.name, weight: versionNameWeight },
];

interface ColumnOptions {
  onToggleRow: (row: QuickSearchVulnerabilityListItem) => void;
  /**
   * The row the panel is open on, or null when it is closed.
   */
  openRowKey: string | null;
  vulnerabilityId: string;
}

const getColumns = ({
  onToggleRow,
  openRowKey,
  vulnerabilityId,
}: ColumnOptions): ColumnDef<QuickSearchVulnerabilityListItem>[] => [
  {
    id: 'versionName',
    accessorFn: row => row.appVersion.name,
    header: 'Version Name',
    meta: {
      description:
        "The affected application and version; the name links to the version's Vulnerabilities tab, filtered to this vulnerability.",
    },
    size: 320,
    sortDescFirst: false,
    cell: ({ row: { original } }) => (
      <div className={s.versionCell}>
        <Link
          className={s.versionName}
          to={`/applications/${original.application.id}/versions/${original.appVersion.id}/vulnerabilities?${new URLSearchParams(
            { vulnerabilityId },
          ).toString()}`}
          underline
        >
          <TruncateSegments
            segments={getNameSegments(original, COLUMN_VERSION_NAME_WEIGHT)}
          />
        </Link>
        {original.appVersion.description ? (
          <div className={s.description}>
            <TruncateText position="top left">
              {original.appVersion.description}
            </TruncateText>
          </div>
        ) : null}
      </div>
    ),
  },
  {
    id: 'status',
    accessorFn: row => row.appVersion.status,
    header: 'Status',
    meta: {
      align: 'center',
      description: <ApplicationVersionStatusDescription />,
      fixedWidth: 11,
    },
    cell: ({ row: { original } }) => (
      <ApplicationVersionStatusDropdown
        aria-label="Version status"
        readOnly
        status={original.appVersion.status}
      />
    ),
  },
  {
    id: 'contact',
    header: 'Contact',
    meta: {
      description: "The user designated as the application's primary contact.",
    },
    enableSorting: false,
    size: 150,
    cell: ({ row: { original } }) => {
      const { email, name, phone } = original.application.contact ?? {};

      if (!name) return <span className={s.placeholder}>N/A</span>;

      return (
        <ContactCard
          email={email ?? undefined}
          name={name}
          phone={phone ?? undefined}
        />
      );
    },
  },
  {
    id: 'created',
    accessorFn: row => row.appVersion.createdAt,
    header: 'Created',
    meta: { align: 'center', description: 'When this version was created.' },
    size: 110,
    cell: ({ getValue }) => (
      <RelativeTimestamp date={getValue<string>()} capitalize />
    ),
  },
  {
    id: 'releaseDate',
    accessorFn: row => row.appVersion.releaseDate,
    header: 'Release Date',
    meta: {
      align: 'center',
      description: 'The planned or actual release date for this version.',
    },
    size: 120,
    cell: ({ getValue }) => {
      const releaseDate = getValue<string | null>();

      return releaseDate ? (
        <RelativeTimestamp date={releaseDate} capitalize />
      ) : (
        'N/A'
      );
    },
  },
  {
    id: 'hasFix',
    accessorKey: 'hasFix',
    header: 'Fix',
    meta: {
      align: 'center',
      description:
        'Indicates whether one or more affected packages have a fixed version available.',
      fixedWidth: 6,
    },
    cell: ({ getValue }) =>
      getValue<boolean>() ? (
        <Popup
          trigger={
            <Wrench
              className={s.fixIcon}
              color="var(--sui-color-teal)"
              aria-label="Fix available"
            />
          }
          position="bottom left"
          content="A fixed version is available for one or more affected packages."
        />
      ) : null,
  },
  {
    id: 'affectedPackages',
    accessorKey: 'affectedPackages',
    header: 'Packages',
    meta: {
      align: 'center',
      description: AFFECTED_PACKAGES_COLUMN_DESCRIPTION,
    },
    size: 100,
    cell: ({ row }) => (
      <RowDetailPanelTrigger
        expanded={openRowKey === getRowKey(row.original)}
        label={`View packages for ${getRowTitle(row.original)}`}
        onToggle={() => onToggleRow(row.original)}
      >
        {row.original.affectedPackages}
      </RowDetailPanelTrigger>
    ),
  },
];

export interface ImpactedAppVersionsTableProps {
  /**
   * The vulnerability being searched, used to deep-link each row to the
   * version's filtered Vulnerabilities tab.
   */
  vulnerabilityId: string;
  /**
   * The vulnerability database build date, shown in the empty state.
   */
  builtAt?: string | null;
  /**
   * The affected application versions accumulated across fetched pages.
   */
  data: QuickSearchVulnerabilityListItem[];
  /**
   * A failed fetch, rendered in place of the rows (the section header stays).
   */
  error?: FetchBaseQueryError | SerializedError;
  /**
   * Whether filters are actively narrowing the results, which changes the
   * empty-state message.
   */
  filtering?: boolean;
  /**
   * Fetches more rows as the windowed tail is reached.
   */
  infinite?: InfiniteScrollOptions;
  /**
   * Whether the first page is still loading.
   */
  loading?: boolean;
  /**
   * Callback fired when the user changes the sort order.
   */
  onSortChange?: (sort: SortingState) => void;
  /**
   * The current sort state, used to display the active sort indicator.
   */
  sort?: PaginationSort;
}

/**
 * Sortable, windowed table of the application versions affected by a
 * vulnerability.
 */
export const ImpactedAppVersionsTable = ({
  vulnerabilityId,
  builtAt,
  data,
  error,
  filtering = false,
  infinite,
  loading = false,
  onSortChange,
  sort,
}: ImpactedAppVersionsTableProps) => {
  useStyles(s);

  const { headerRef } = usePageContext();
  const tableInstanceRef =
    useRef<DataTableInstance<QuickSearchVulnerabilityListItem> | null>(null);

  const { activeRowKey, isOpen, isRowActive, toggleRow } =
    useRowDetailPanel<QuickSearchVulnerabilityListItem>({
      data,
      getRowKey,
      infinite,
      panelOptions: DETAIL_PANEL_OPTIONS,
      render: ({
        hasNext,
        hasPrev,
        isFilteredOut,
        onClose,
        onNext,
        onPrev,
        row,
      }) => ({
        content: (
          <AffectedPackagesPanel
            appId={row.application.id}
            versionId={row.appVersion.id}
            vulnerabilityId={vulnerabilityId}
            entity={PANEL_ENTITY}
            filteredOut={isFilteredOut}
          />
        ),
        header: (
          <ContextPanel.Header onClose={onClose}>
            <AffectedPackagesPanelHeader
              title={
                <TruncateSegments
                  segments={getNameSegments(row, HEADER_VERSION_NAME_WEIGHT)}
                />
              }
              entity={PANEL_ENTITY}
              hasPrev={hasPrev}
              hasNext={hasNext}
              onPrev={onPrev}
              onNext={onNext}
            />
          </ContextPanel.Header>
        ),
      }),
      scrollToIndex: (index, options) =>
        tableInstanceRef.current?.scrollToIndex(index, options),
    });

  // Collapsed here rather than passed as two values, so a row cannot read as
  // active while the panel is shut.
  const openRowKey = isOpen ? activeRowKey : null;

  const columns = useMemo(
    () =>
      getColumns({
        onToggleRow: toggleRow,
        openRowKey,
        vulnerabilityId,
      }),
    [openRowKey, toggleRow, vulnerabilityId],
  );

  if (error) return <Err error={error} imageSize="medium" noBorder />;

  return (
    <ServerDataTable
      aria-label="Impacted Application Versions"
      className={s.root}
      columns={columns}
      data={data}
      filtering={filtering}
      infinite={infinite}
      instanceRef={tableInstanceRef}
      isRowActive={row => isRowActive(row.original)}
      loading={loading}
      // An explicit title bypasses the placeholder's own filtering copy, so
      // only the supporting message varies.
      noData={
        <EmptyPlaceholder
          illustration={<img src="/img/robot_no_findings.svg" alt="" />}
          title="No Findings to Display"
          docsMessage={
            filtering
              ? 'No impacted application versions for the current filters'
              : noImpactedVersionsMessage(builtAt)
          }
        />
      }
      onRowClick={row => toggleRow(row.original)}
      onSortChange={onSortChange}
      resizable
      sort={convertToTableSort(sort)}
      stickyHeader={headerRef}
    />
  );
};

export default ImpactedAppVersionsTable;
